/**
 * Insights library (spec §18): every insight is a pure builder over the year
 * snapshot array — testable without React — rendered by one generic chart.
 * All respect the today's-dollars toggle and export to CSV and PNG.
 */
import type { YearSnapshot } from '../engine/snapshot';

export type InsightKind = 'stacked-area' | 'line' | 'bar';

export interface InsightSeries {
  key: string;
  /** CSS var for the stroke/fill. */
  color: string;
}

export interface InsightData {
  kind: InsightKind;
  /** Values are numbers keyed by series key, plus `year`. */
  data: Record<string, number>[];
  series: InsightSeries[];
  /** Y values are percentages (0-1 scaled to %). */
  percent?: boolean;
  note: string;
}

export interface InsightDef {
  key: string;
  label: string;
  group: 'Cash flow' | 'Taxes' | 'Retirement income';
  build: (snaps: YearSnapshot[], todaysDollars: boolean) => InsightData;
}

const C1 = 'var(--series-1)';
const C2 = 'var(--series-2)';
const C3 = 'var(--series-3, #7c9a5e)';
const C4 = 'var(--series-4, #9a6ab8)';
const C5 = 'var(--series-5, #c2733a)';
const C6 = 'var(--ink-muted)';

function defl(s: YearSnapshot, v: number, today: boolean): number {
  return Math.round(today ? v / s.cumulativeInflationFactor : v);
}

export const INSIGHTS: InsightDef[] = [
  {
    key: 'income_by_source', label: 'Income by source', group: 'Cash flow',
    build: (snaps, t) => ({
      kind: 'stacked-area',
      series: [
        { key: 'Work', color: C1 },
        { key: 'Social Security', color: C2 },
        { key: 'Pension & annuity', color: C3 },
        { key: 'Rental & other', color: C4 },
        { key: 'RMDs', color: C5 },
        { key: 'Other withdrawals', color: C6 },
      ],
      data: snaps.map((s) => ({
        year: s.year,
        Work: defl(s, s.wagesIncome + s.selfEmploymentIncome, t),
        'Social Security': defl(s, s.socialSecurityGross, t),
        'Pension & annuity': defl(s, s.pensionIncome + s.annuityIncome, t),
        'Rental & other': defl(s, s.rentalRoyaltyOtherIncome + s.oneTimeInflows, t),
        RMDs: defl(s, s.totalRmd, t),
        'Other withdrawals': defl(s, s.totalWithdrawals, t),
      })),
      note: 'Where each year\'s money comes from. Watch work income hand off to withdrawals, then Social Security and RMDs.',
    }),
  },
  {
    key: 'expenses_by_category', label: 'Expenses by category', group: 'Cash flow',
    build: (snaps, t) => ({
      kind: 'stacked-area',
      series: [
        { key: 'Essential', color: C1 },
        { key: 'Discretionary', color: C2 },
        { key: 'Healthcare + LTC', color: C3 },
        { key: 'Debt', color: C4 },
        { key: 'Property', color: C5 },
        { key: 'Taxes paid', color: C6 },
      ],
      data: snaps.map((s) => ({
        year: s.year,
        Essential: defl(s, s.essentialSpending, t),
        Discretionary: defl(s, s.discretionarySpending, t),
        'Healthcare + LTC': defl(s, s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost, t),
        Debt: defl(s, s.debtService, t),
        Property: defl(s, s.propertyCosts + s.propertyPurchaseOutflow, t),
        'Taxes paid': defl(s, s.priorYearTaxPaid, t),
      })),
      note: 'Every outflow, stacked. Healthcare grows on its own inflation track; debt service drops off a cliff at payoff.',
    }),
  },
  {
    key: 'net_cash_flow', label: 'Net cash flow', group: 'Cash flow',
    build: (snaps, t) => ({
      kind: 'bar',
      series: [{ key: 'Net cash flow', color: C1 }],
      data: snaps.map((s) => ({
        year: s.year,
        'Net cash flow': defl(s, s.surplusToSavings - s.unfundedSpending - s.totalWithdrawals, t),
      })),
      note: 'Positive: saving after all bills. Negative: the portfolio funded the difference (normal in retirement).',
    }),
  },
  {
    key: 'net_worth', label: 'Net worth over time', group: 'Cash flow',
    build: (snaps, t) => ({
      kind: 'line',
      series: [
        { key: 'Net worth', color: C1 },
        { key: 'Real estate', color: C3 },
      ],
      data: snaps.map((s) => ({
        year: s.year,
        'Net worth': defl(s, s.netWorth, t),
        'Real estate': defl(s, s.realEstateValue, t),
      })),
      note: 'Total net worth including home equity, with the real-estate portion shown separately.',
    }),
  },
  {
    key: 'tax_by_jurisdiction', label: 'Taxes by type', group: 'Taxes',
    build: (snaps, t) => ({
      kind: 'stacked-area',
      series: [
        { key: 'Federal income', color: C1 },
        { key: 'Payroll (FICA)', color: C2 },
        { key: 'State', color: C3 },
        { key: 'Penalties & recapture', color: C5 },
      ],
      data: snaps.map((s) => ({
        year: s.year,
        'Federal income': defl(s, s.federal.totalTax, t),
        'Payroll (FICA)': defl(s, s.fica.total, t),
        State: defl(s, s.stateTax, t),
        'Penalties & recapture': defl(s, s.earlyWithdrawalPenalty + s.recaptureTaxEstimate, t),
      })),
      note: 'The full tax bill by type. FICA ends with the paycheck; the retirement question is the federal line.',
    }),
  },
  {
    key: 'rates', label: 'Marginal vs. effective rate', group: 'Taxes',
    build: (snaps) => ({
      kind: 'line',
      percent: true,
      series: [
        { key: 'Marginal', color: C2 },
        { key: 'Effective', color: C1 },
      ],
      data: snaps.map((s) => ({
        year: s.year,
        Marginal: Math.round(s.federal.marginalOrdinaryRate * 1000) / 10,
        Effective: Math.round(s.federal.effectiveRate * 1000) / 10,
      })),
      note: 'The marginal rate is what the NEXT dollar costs — the number that matters for conversions and withdrawals.',
    }),
  },
  {
    key: 'cumulative_tax', label: 'Lifetime taxes, cumulative', group: 'Taxes',
    build: (snaps, t) => {
      let run = 0;
      return {
        kind: 'stacked-area',
        series: [{ key: 'Cumulative taxes', color: C2 }],
        data: snaps.map((s) => {
          run += t ? s.totalTaxLiability / s.cumulativeInflationFactor : s.totalTaxLiability;
          return { year: s.year, 'Cumulative taxes': Math.round(run) };
        }),
        note: 'Every tax dollar over the whole plan, accumulating. Strategy changes bend this curve.',
      };
    },
  },
  {
    key: 'irmaa', label: 'Medicare surcharge (IRMAA) tier', group: 'Taxes',
    build: (snaps) => ({
      kind: 'bar',
      series: [{ key: 'IRMAA tier', color: C2 }],
      data: snaps.map((s) => ({ year: s.year, 'IRMAA tier': s.irmaaTierIndex })),
      note: 'Tier 0 = no surcharge. Each step up costs $1,000-2,900 per person per year, decided by income two years earlier.',
    }),
  },
  {
    key: 'withdrawals_by_account', label: 'Withdrawals by account', group: 'Retirement income',
    build: (snaps, t) => {
      // Top accounts by lifetime withdrawals, rest bundled.
      const totals = new Map<string, number>();
      for (const s of snaps) {
        for (const f of s.accounts) {
          totals.set(f.label, (totals.get(f.label) ?? 0) + f.withdrawal);
        }
      }
      const top = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5)
        .filter(([, v]) => v > 0).map(([k]) => k);
      const colors = [C1, C2, C3, C4, C5];
      return {
        kind: 'stacked-area',
        series: top.map((k, i) => ({ key: k, color: colors[i]! })),
        data: snaps.map((s) => {
          const row: Record<string, number> = { year: s.year };
          for (const k of top) {
            const f = s.accounts.find((x) => x.label === k);
            row[k] = f ? defl(s, f.withdrawal, t) : 0;
          }
          return row;
        }),
        note: 'Which accounts fund retirement, year by year — the withdrawal order made visible.',
      };
    },
  },
  {
    key: 'withdrawal_rate', label: 'Withdrawal rate over time', group: 'Retirement income',
    build: (snaps) => ({
      kind: 'line',
      percent: true,
      series: [{ key: 'Withdrawal rate', color: C1 }],
      data: snaps.map((s) => {
        const portfolio = s.accounts.reduce((tt, f) => tt + f.startBalance, 0);
        const rate = portfolio > 0 ? (s.totalWithdrawals + s.totalRmd) / portfolio : 0;
        return { year: s.year, 'Withdrawal rate': Math.round(rate * 1000) / 10 };
      }),
      note: 'Distributions as a share of the portfolio. Sustained rates above ~5% lean hard on good markets.',
    }),
  },
  {
    key: 'rmd', label: 'RMD schedule', group: 'Retirement income',
    build: (snaps, t) => ({
      kind: 'bar',
      series: [{ key: 'Required distributions', color: C5 }],
      data: snaps.map((s) => ({
        year: s.year,
        'Required distributions': defl(s, s.totalRmd, t),
      })),
      note: 'Income the IRS forces out of pre-tax accounts whether needed or not. Roth conversions shrink these bars.',
    }),
  },
  {
    key: 'conversions', label: 'Roth conversions', group: 'Retirement income',
    build: (snaps, t) => ({
      kind: 'bar',
      series: [{ key: 'Converted', color: C3 }],
      data: snaps.map((s) => ({
        year: s.year,
        Converted: defl(s, s.rothConversionAmount, t),
      })),
      note: 'The conversion schedule in effect — money moved from pre-tax to Roth, taxed now at known rates.',
    }),
  },
];

/** CSV export of any insight's data. */
export function insightToCsv(d: InsightData): string {
  const keys = ['year', ...d.series.map((s) => s.key)];
  const lines = [keys.join(',')];
  for (const row of d.data) {
    lines.push(keys.map((k) => String(row[k] ?? 0)).join(','));
  }
  return lines.join('\n');
}
